import { ARENA_H, ARENA_W, PLAYER } from "../config.ts";
import { clamp } from "../core/vec.ts";

/**
 * Unified input abstraction. The game only reads `InputState` and never cares
 * whether it came from touch, mouse, or keyboard.
 *
 * Mobile (primary): offset drag-to-move. The finger drags anywhere; the avatar
 * target is the finger position shifted UP by PLAYER.touchOffsetY so the finger
 * never occludes the avatar (the standard bullet-hell-on-touch solution). Every
 * new touchdown fires a `tap` (reserved for the sword, Phase 3+) immediately —
 * no release-and-check-if-it-moved — so the player can slash the instant their
 * finger lands and keep sliding without breaking the swing.
 *
 * Desktop: WASD/arrow keys drive a velocity target; mouse can also move the
 * avatar directly (pointer-follow); Space/click = tap.
 */

export interface InputState {
  /** True while a positional pointer (touch/mouse-drag) is steering. */
  pointerActive: boolean;
  /** Desired avatar position in arena space (only meaningful if pointerActive). */
  targetX: number;
  targetY: number;
  /** Keyboard movement axis, each in [-1, 1]. */
  axisX: number;
  axisY: number;
  /** Edge-triggered tap (consumed by the reader). */
  tapped: boolean;
}

interface ScreenToArena {
  (clientX: number, clientY: number, out: { x: number; y: number }): void;
}

export class Input {
  readonly state: InputState = {
    pointerActive: false,
    targetX: ARENA_W / 2,
    targetY: ARENA_H * 0.78,
    axisX: 0,
    axisY: 0,
    tapped: false,
  };

  private keys = new Set<string>();
  private activePointerId: number | null = null;
  private readonly tmp = { x: 0, y: 0 };
  private usingTouch = false;

  /** @param mapToArena converts client (CSS) coords into arena logical coords. */
  constructor(
    el: HTMLElement,
    private readonly mapToArena: ScreenToArena,
  ) {
    el.addEventListener("pointerdown", this.onDown, { passive: false });
    el.addEventListener("pointermove", this.onMove, { passive: false });
    window.addEventListener("pointerup", this.onUp, { passive: false });
    window.addEventListener("pointercancel", this.onUp, { passive: false });
    window.addEventListener("keydown", this.onKey);
    window.addEventListener("keyup", this.onKey);
    el.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private onDown = (e: PointerEvent): void => {
    e.preventDefault();
    // Every touchdown slashes immediately — the first finger landing, a
    // second finger while already dragging, or a fresh finger after lifting
    // off and placing down again. Movement keeps tracking the same pointer
    // (or switches to the new one) without interrupting the swing.
    this.state.tapped = true;
    if (this.activePointerId !== null) return;
    this.activePointerId = e.pointerId;
    this.usingTouch = e.pointerType === "touch";
    this.applyPointer(e.clientX, e.clientY);
    this.state.pointerActive = true;
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    e.preventDefault();
    this.applyPointer(e.clientX, e.clientY);
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    this.activePointerId = null;
    this.state.pointerActive = false;
  };

  private applyPointer(clientX: number, clientY: number): void {
    this.mapToArena(clientX, clientY, this.tmp);
    // Float the avatar above the finger on touch so it stays visible.
    const offset = this.usingTouch ? PLAYER.touchOffsetY : 0;
    this.state.targetX = clamp(this.tmp.x, 0, ARENA_W);
    this.state.targetY = clamp(this.tmp.y - offset, 0, ARENA_H);
  }

  private onKey = (e: KeyboardEvent): void => {
    const down = e.type === "keydown";
    const k = e.key.toLowerCase();
    if (
      k === "arrowup" ||
      k === "arrowdown" ||
      k === "arrowleft" ||
      k === "arrowright" ||
      k === " "
    ) {
      e.preventDefault();
    }
    if (down && (k === " " || k === "enter")) this.pendingKeyTap = true;
    if (down) this.keys.add(k);
    else this.keys.delete(k);
    this.recomputeAxes();
  };

  private pendingKeyTap = false;

  private recomputeAxes(): void {
    const k = this.keys;
    let x = 0;
    let y = 0;
    if (k.has("arrowleft") || k.has("a")) x -= 1;
    if (k.has("arrowright") || k.has("d")) x += 1;
    if (k.has("arrowup") || k.has("w")) y -= 1;
    if (k.has("arrowdown") || k.has("s")) y += 1;
    this.state.axisX = x;
    this.state.axisY = y;
  }

  /** Call once per frame after the game has read `tapped`. */
  endFrame(): void {
    this.state.tapped = false;
    if (this.pendingKeyTap) {
      this.pendingKeyTap = false;
    }
  }

  /** Read+consume a tap edge (used for menu/sword). */
  consumeTap(): boolean {
    if (this.state.tapped || this.pendingKeyTap) {
      this.state.tapped = false;
      this.pendingKeyTap = false;
      return true;
    }
    return false;
  }

  /** Is the keyboard currently driving movement? */
  get keyboardActive(): boolean {
    return this.state.axisX !== 0 || this.state.axisY !== 0;
  }
}
